import api from "kw/api";
import { GraphQLTypes } from "kw/api/zeus";
import { QuestionFlowTypeKey } from "kw/components/QuestionFlow/QuestionFlowComponent";
import { quoteFields, riskProfileFields } from "kw/api/fragments";
import { useEffect } from "react";
import { useGlobal } from "kanopi-components/useGlobal";
import { useTaskPoll } from "kanopi-components/useTaskPoll";

export type TuseQuoteDocumentsPollArgs = {
  riskProfileId: string;
  shouldPollDocuments?: boolean;
  globalDataKey?: string;
};

export type TuseQuoteDocumentsPoll = {
  documents: GraphQLTypes["QuoteType"]["documents"];
  documentsLoading: boolean;
};

export const useQuoteDocumentsPoll = ({
  riskProfileId,
  shouldPollDocuments,
  globalDataKey = QuestionFlowTypeKey.quote,
}: TuseQuoteDocumentsPollArgs): TuseQuoteDocumentsPoll => {
  const [data, setData] = useGlobal(globalDataKey);
  async function queryQuoteDocumentsStatus() {
    const response = await api.query({
      riskProfile: [
        {
          id: riskProfileId,
        },
        {
          quoteDocumentGenerateStatus: true,
        },
      ],
    });
    return response.riskProfile.quoteDocumentGenerateStatus;
  }

  async function queryQuoteDocuments() {
    const response = await api.query({
      riskProfile: [
        {
          id: riskProfileId,
        },
        {
          ...riskProfileFields,
          quotes: {
            ...quoteFields,
          },
        },
      ],
    });
    return (response.riskProfile?.quotes || []).reduce(
      (a, b) => [...a, ...(b?.documents || [])],
      []
    );
  }

  const { content, loading, taskPoll } = useTaskPoll({
    poll: queryQuoteDocumentsStatus,
    query: queryQuoteDocuments,
  });

  // Retrigger the poll if we have repriced the quote. We know this
  // because if shouldPollDocuments changes, and it's false, it means we've just
  // finished pricing.
  useEffect(() => {
    if (!shouldPollDocuments) {
      taskPoll();
    }
  }, [shouldPollDocuments]);

  return { documents: content, documentsLoading: loading || shouldPollDocuments };
};
